import type { Accessor } from 'solid-js'
import { intlLocale } from '~/lib/params'
import { m } from '~/paraglide/messages'
import type { CoinProduct } from '~/services/currency-catalogue'
import type { ShopBalances } from '~/store/session-ledger'
import type { CurrencyArtworkStatus } from './currency-artwork'
import { CurrencyRewardReveal } from './currency-reward-reveal'
import {
  ConfirmPriceCta,
  createEffectivePriceParts,
} from './effective-price-parts'
import {
  captureSubmittedAmount,
  softPurchaseAdapter,
} from './purchase-flow/adapters'
import { PurchaseFlow } from './purchase-flow/surface'

const numberFormatter = new Intl.NumberFormat(intlLocale)

export function CurrencyExchangeFlow(props: {
  artworkStatus: Accessor<CurrencyArtworkStatus>
  balances: Accessor<ShopBalances | undefined>
  credit: (currency: keyof ShopBalances, amount: number) => void
  debit: (currency: keyof ShopBalances, amount: number) => void
  onCancel: () => void
  onComplete: () => void
  onArtworkError: () => void
  onArtworkLoad: () => void
  product: CoinProduct
  serverNow: Accessor<number>
  userId: number
}) {
  // Live effective price (ADR 0027): the confirmation re-derives as the
  // server clock crosses a sale-window edge — no snapshot at open.
  const { amount, original } = createEffectivePriceParts(
    () => props.product.pricing,
    () => props.serverNow(),
  )
  const { adapter, submittedAmount } = captureSubmittedAmount(
    softPurchaseAdapter({
      // CoinProduct.currency is the granted currency, not the payment
      // currency — the exchange always pays in Time Crystals, so the
      // generic canAfford(product) shape does not apply here.
      affordable: () => (props.balances()?.gem ?? 0) >= amount(),
      productId: () => props.product.productId,
      userId: () => props.userId,
    }),
    amount,
  )

  return (
    <PurchaseFlow
      adapter={adapter}
      balancePills={props.balances}
      confirmation={{
        cta: (
          <ConfirmPriceCta
            amount={amount()}
            currency="gem"
            original={original()}
          />
        ),
        description: m.purchase_confirm_general({
          currency: m.time_crystal(),
          number: numberFormatter.format(props.product.grant),
          price: numberFormatter.format(amount()),
        }),
        title: m.dialog_confirm_purchase_title(),
      }}
      insufficient={{
        description: m.dialog_not_enough_gems_description(),
        kind: 'dialog',
        title: m.dialog_not_enough_balance_gem_title(),
      }}
      onCancel={() => props.onCancel()}
      onCommitted={() => props.debit('gem', submittedAmount())}
      reveal={() => (
        <CurrencyRewardReveal
          artworkStatus={props.artworkStatus}
          balanceTestIdPrefix="currency-exchange-"
          balances={props.balances}
          onArtworkError={props.onArtworkError}
          onArtworkLoad={props.onArtworkLoad}
          onArrival={() => {
            props.credit('coin', props.product.grant)
            props.onComplete()
          }}
          product={props.product}
          testIdPrefix="coin-reward"
        />
      )}
      testIdPrefix="currency-exchange"
    />
  )
}
